import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import usePagedRegister from "../usePagedRegister";
import "./MemberWorkspace.css";

const list = (response) => response.data?.results || response.data || [];
const money = (value) => Number(value || 0).toFixed(2);
const message = (error) =>
  error.response?.data?.detail || "The request could not be completed.";

const EDITABLE_TEXT_FIELDS = [
  "first_name",
  "surname",
  "national_id",
  "phone",
  "cell",
  "email",
  "employer",
  "occupation",
  "work_number",
  "bank_name",
  "account_number",
  "bank_branch",
  "address",
];

const labelFor = (field) =>
  field
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

export default function MemberWorkspace() {
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("details");
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [openingEdit, setOpeningEdit] = useState(false);

  const {
    items: members,
    reload: reloadMembers,
    onScroll: memberScroll,
  } = usePagedRegister("members/", {
    search,
    pageSize: 50,
  });

  const load = async () => reloadMembers({ append: false });

  useEffect(() => {
    load().catch(() => toast.error("Could not load members."));
  }, []);

  const open = async (id) => {
    const response = await axiosInstance.get(`members/${id}/`);
    setSelected(response.data);
  };

  const shown = useMemo(
    () =>
      members.filter((member) =>
        [
          member.code,
          member.policy_code,
          member.full_name,
          member.national_id,
          member.phone,
          member.cell,
        ]
          .join(" ")
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [members, search]
  );

  const beginEdit = async (memberOrId) => {
    const id =
      typeof memberOrId === "object" ? memberOrId.id : memberOrId;

    setOpeningEdit(true);
    try {
      const response = await axiosInstance.get(`members/${id}/`);
      setEditTarget(response.data);
      setForm(response.data);
      setReason("");
    } catch (error) {
      toast.error(message(error));
    } finally {
      setOpeningEdit(false);
    }
  };

  const closeEdit = () => {
    setEditTarget(null);
    setForm({});
    setReason("");
  };

  const save = async (event) => {
    event.preventDefault();

    if (!editTarget) return;

    try {
      await axiosInstance.patch(
        `members/${editTarget.id}/update/`,
        { ...form, reason }
      );

      const isPolicyholder = editTarget.id === selected?.id;
      toast.success(
        isPolicyholder
          ? "Policyholder updated."
          : "Dependant updated."
      );

      closeEdit();

      if (selected?.id) {
        await open(selected.id);
      }
      await load();
    } catch (error) {
      toast.error(message(error));
    }
  };

  const remove = async () => {
    if (!selected) return;

    const why = window.prompt("Reason for removing this member");
    if (!why) return;

    try {
      await axiosInstance.post(
        `members/${selected.id}/remove/`,
        { reason: why }
      );
      toast.success("Member removed.");
      await open(selected.id);
      await load();
    } catch (error) {
      toast.error(message(error));
    }
  };

  return (
    <section className="member-page">
      <header>
        <span>ASSURANCE OPERATIONS</span>
        <h1>Members</h1>
        <p>
          Search policyholders and manage the beneficiaries covered
          under each policy.
        </p>
      </header>

      <div className="member-layout">
        <aside>
          <input
            placeholder="Search policyholder, policy, ID or phone"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          <div className="member-list">
            {shown.map((member) => (
              <button
                className={
                  selected?.id === member.id ? "active" : ""
                }
                onClick={() => open(member.id)}
                key={member.id}
              >
                <strong>{member.code}</strong>
                <span>{member.full_name}</span>
                <small>
                  {member.policy_code} · {member.status}
                </small>
                <b>{money(member.monthly_premium)}</b>
              </button>
            ))}
          </div>
        </aside>

        <main>
          {!selected ? (
            <div className="member-empty">
              Select a policyholder to open the member record and
              beneficiaries.
            </div>
          ) : (
            <>
              <div className="member-head">
                <div>
                  <span>MEMBER RECORD</span>
                  <h2>{selected.code}</h2>
                  <p>
                    {selected.full_name} · {selected.policy_code}
                  </p>
                </div>

                <div>
                  <button
                    className="secondary"
                    onClick={() => beginEdit(selected.id)}
                    disabled={openingEdit}
                  >
                    Update member
                  </button>
                  <button className="danger" onClick={remove}>
                    Remove member
                  </button>
                </div>
              </div>

              <div className="member-summary">
                <div>
                  <span>Status</span>
                  <strong>{selected.status}</strong>
                </div>
                <div>
                  <span>Member type</span>
                  <strong>{selected.member_type}</strong>
                </div>
                <div>
                  <span>Premium / month</span>
                  <strong>{money(selected.monthly_premium)}</strong>
                </div>
                <div>
                  <span>Date joined</span>
                  <strong>{selected.date_joined || "—"}</strong>
                </div>
                <div>
                  <span>Inception</span>
                  <strong>{selected.inception_date || "—"}</strong>
                </div>
                <div>
                  <span>Days to inception</span>
                  <strong>
                    {selected.days_to_inception ?? "—"}
                  </strong>
                </div>
                <div>
                  <span>Claims</span>
                  <strong>{selected.claim_count}</strong>
                </div>
              </div>

              <nav className="member-tabs">
                {[
                  ["details", "Member details"],
                  ["other", "Other information"],
                  ["payments", "Payment history"],
                  ["beneficiaries", "View beneficiaries"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    className={tab === id ? "active" : ""}
                    onClick={() => setTab(id)}
                  >
                    {label}
                  </button>
                ))}
              </nav>

              <div className="member-content">
                {tab === "details" && (
                  <div className="detail-grid">
                    {[
                      ["First name", selected.first_name],
                      ["Surname", selected.surname],
                      ["Gender", selected.gender],
                      ["National ID", selected.national_id],
                      ["Date of birth", selected.date_of_birth],
                      ["Relationship", selected.relationship],
                      [
                        "Mourners transport",
                        selected.mourners_transport ? "YES" : "NO",
                      ],
                      [
                        "Disabled",
                        selected.is_disabled ? "YES" : "NO",
                      ],
                      ["Member status", selected.status],
                      [
                        "Monthly premium",
                        money(selected.monthly_premium),
                      ],
                      ["Date joined", selected.date_joined],
                      ["Inception date", selected.inception_date],
                      ["Maturity date", selected.maturity_date],
                    ].map(([label, value]) => (
                      <article key={label}>
                        <span>{label}</span>
                        <strong>{value || "—"}</strong>
                      </article>
                    ))}
                  </div>
                )}

                {tab === "other" && (
                  <div className="detail-grid">
                    {[
                      ["Employer", selected.employer],
                      ["Occupation", selected.occupation],
                      ["EC / Work number", selected.work_number],
                      ["Bank", selected.bank_name],
                      ["Account number", selected.account_number],
                      ["Bank branch", selected.bank_branch],
                      ["Phone", selected.phone],
                      ["Cell number", selected.cell],
                      ["Email", selected.email],
                      ["Address", selected.address],
                      [
                        "Policy start date",
                        selected.policy_start_date,
                      ],
                      [
                        "Plan",
                        `${selected.plan_code || ""} ${
                          selected.plan_name || ""
                        }`,
                      ],
                    ].map(([label, value]) => (
                      <article key={label}>
                        <span>{label}</span>
                        <strong>{value || "—"}</strong>
                      </article>
                    ))}
                  </div>
                )}

                {tab === "payments" && (
                  <table>
                    <thead>
                      <tr>
                        <th>Receipt</th>
                        <th>Date</th>
                        <th>Bill</th>
                        <th>Bill date</th>
                        <th>Bill amount</th>
                        <th>Amount paid</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.payment_history?.map((item) => (
                        <tr key={item.id}>
                          <td>{item.code}</td>
                          <td>{item.receipt_date}</td>
                          <td>{item.bill_code || "—"}</td>
                          <td>{item.bill_date || "—"}</td>
                          <td>{money(item.bill_amount)}</td>
                          <td>{money(item.amount)}</td>
                          <td>{money(item.bill_balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {tab === "beneficiaries" && (
                  <div className="covered-beneficiaries">
                    <div className="beneficiary-heading">
                      <div>
                        <h3>Covered beneficiaries</h3>
                        <p>
                          Select Update to correct a spouse, child or
                          other dependant without moving them into the
                          policyholder register.
                        </p>
                      </div>
                      <span>
                        {selected.covered_beneficiaries?.length || 0}{" "}
                        records
                      </span>
                    </div>

                    <table className="beneficiary-table">
                      <thead>
                        <tr>
                          <th>Member ID</th>
                          <th>Name</th>
                          <th>Type</th>
                          <th>Relationship</th>
                          <th>Date of birth</th>
                          <th>National ID</th>
                          <th>Phone</th>
                          <th>Date joined</th>
                          <th>Inception</th>
                          <th>Premium</th>
                          <th>Status</th>
                          <th aria-label="Actions" />
                        </tr>
                      </thead>
                      <tbody>
                        {selected.covered_beneficiaries?.length ? (
                          selected.covered_beneficiaries.map(
                            (dependant) => (
                              <tr key={dependant.id}>
                                <td>{dependant.code}</td>
                                <td>
                                  <strong>
                                    {dependant.full_name}
                                  </strong>
                                </td>
                                <td>{dependant.member_type}</td>
                                <td>
                                  {dependant.relationship || "—"}
                                </td>
                                <td>
                                  {dependant.date_of_birth || "—"}
                                </td>
                                <td>
                                  {dependant.national_id || "—"}
                                </td>
                                <td>
                                  {dependant.cell ||
                                    dependant.phone ||
                                    "—"}
                                </td>
                                <td>
                                  {dependant.date_joined || "—"}
                                </td>
                                <td>
                                  {dependant.inception_date || "—"}
                                </td>
                                <td>
                                  {money(
                                    dependant.monthly_premium
                                  )}
                                </td>
                                <td>{dependant.status}</td>
                                <td className="beneficiary-actions">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      beginEdit(dependant.id)
                                    }
                                    disabled={openingEdit}
                                  >
                                    Update
                                  </button>
                                </td>
                              </tr>
                            )
                          )
                        ) : (
                          <tr>
                            <td
                              colSpan="12"
                              className="empty-row"
                            >
                              No spouse, children or other
                              dependants are linked to this
                              policyholder.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {editTarget && (
        <div className="member-modal">
          <form onSubmit={save}>
            <header>
              <div>
                <span>
                  {editTarget.member_type === "POLICY HOLDER"
                    ? "POLICYHOLDER"
                    : "DEPENDANT"}
                </span>
                <h3>
                  Update {editTarget.full_name}
                </h3>
                <p>
                  {editTarget.code} · {editTarget.member_type}
                </p>
              </div>
              <button type="button" onClick={closeEdit}>
                ×
              </button>
            </header>

            <div>
              {EDITABLE_TEXT_FIELDS.map((field) => (
                <label key={field}>
                  {labelFor(field)}
                  <input
                    value={form[field] || ""}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        [field]: event.target.value,
                      })
                    }
                  />
                </label>
              ))}

              <label>
                Date of birth
                <input
                  type="date"
                  value={form.date_of_birth || ""}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      date_of_birth: event.target.value,
                    })
                  }
                />
              </label>

              <label>
                Date joined
                <input
                  type="date"
                  value={form.date_joined || ""}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      date_joined: event.target.value,
                    })
                  }
                />
              </label>

              <label>
                Inception date
                <input
                  type="date"
                  value={form.inception_date || ""}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      inception_date: event.target.value,
                    })
                  }
                />
              </label>

              <label>
                Gender
                <select
                  value={form.gender || ""}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      gender: event.target.value,
                    })
                  }
                >
                  <option value="">Select gender</option>
                  <option value="MALE">MALE</option>
                  <option value="FEMALE">FEMALE</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </label>

              <label>
                Relationship
                <input
                  value={form.relationship || ""}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      relationship: event.target.value,
                    })
                  }
                />
              </label>

              <label>
                Member status
                <select
                  value={form.status || ""}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      status: event.target.value,
                    })
                  }
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                  <option value="REMOVED">REMOVED</option>
                  <option value="DECEASED">DECEASED</option>
                </select>
              </label>

              <label className="member-checkbox">
                <input
                  type="checkbox"
                  checked={Boolean(form.is_disabled)}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      is_disabled: event.target.checked,
                    })
                  }
                />
                Member has a disability
              </label>

              <label className="member-checkbox">
                <input
                  type="checkbox"
                  checked={Boolean(form.mourners_transport)}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      mourners_transport: event.target.checked,
                    })
                  }
                />
                Mourners transport
              </label>

              <label className="member-reason">
                Reason for update
                <textarea
                  value={reason}
                  onChange={(event) =>
                    setReason(event.target.value)
                  }
                  required
                  placeholder="Explain why this member record is being changed"
                />
              </label>
            </div>

            <footer>
              <button
                type="button"
                className="secondary"
                onClick={closeEdit}
              >
                Cancel
              </button>
              <button>
                Save changes
              </button>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
}
